import { IsoDate } from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { DashboardLink } from '@partner/feature-modules/dashboard/domain/model/dashboard-link.model';
import {
  PartnerScorecard,
  SCORECARD_SERIES,
  ScorecardChart,
  ScorecardDeclinedReasons,
  ScorecardMonth,
  ScorecardSeries,
} from '@partner/feature-modules/dashboard/domain/model/partner-scorecard.model';
import { PipelineRatio } from '@partner/feature-modules/dashboard/domain/model/pipeline-health.model';
import { WebPaths } from '@partner/shared/SharedConstants';
import { adaptDashboardLink, DashboardLinkResponse } from './dashboard-link-api.model';
import { PipelineRatioResponse } from './pipeline-health-api.model';

/** 200 body of `GET api/dashboard/partner-scorecard` (contract v1.0); instants are ISO-8601 UTC strings. */
export interface PartnerScorecardResponse {
  asOfUtc: string;
  window: { from: IsoDate; to: IsoDate };
  months: ScorecardMonthResponse[];
  best: Record<ScorecardSeries, number | null>;
  declinedReasons: ScorecardDeclinedReasonsResponse;
  links: Record<ScorecardChart, DashboardLinkResponse | null>;
  /** Envelope symmetry with the sibling responses; always null on 200. */
  validationMessage: string | null;
}

export interface ScorecardMonthResponse {
  month: string;
  fundedAmount: number;
  fundedUnits: number;
  submitted: number;
  approved: number;
  approvalRate: PipelineRatioResponse;
  bookingRate: PipelineRatioResponse;
}

export interface ScorecardDeclinedReasonResponse {
  reason: string;
  count: number;
  rank: number;
}

export interface ScorecardDeclinedReasonsResponse {
  reasons: ScorecardDeclinedReasonResponse[];
  remainingReasons: number;
  remainingCount: number;
}

/** The contract's closed route set. */
export const PARTNER_SCORECARD_ROUTES: readonly string[] = [WebPaths.Submissions];

/** `??` turns a missing percent into null and leaves a real 0 alone. */
const adaptRatio = (ratio: PipelineRatioResponse): PipelineRatio => ({
  percent: ratio.percent ?? null,
  numerator: ratio.numerator,
  denominator: ratio.denominator,
});

const adaptMonth = (month: ScorecardMonthResponse): ScorecardMonth => ({
  month: month.month,
  fundedAmount: month.fundedAmount,
  fundedUnits: month.fundedUnits,
  submitted: month.submitted,
  approved: month.approved,
  approvalRate: adaptRatio(month.approvalRate),
  bookingRate: adaptRatio(month.bookingRate),
});

const adaptDeclinedReasons = (declined: ScorecardDeclinedReasonsResponse): ScorecardDeclinedReasons => ({
  reasons: (declined.reasons ?? []).map((reason: ScorecardDeclinedReasonResponse) => ({
    reason: reason.reason,
    count: reason.count,
    rank: reason.rank,
  })),
  remainingReasons: declined.remainingReasons,
  remainingCount: declined.remainingCount,
});

/** Null for a missing link or a route outside the closed set: that card shows no button. */
function adaptLink(link: DashboardLinkResponse | null | undefined): DashboardLink | null {
  return link && PARTNER_SCORECARD_ROUTES.includes(link.route) ? adaptDashboardLink(link) : null;
}

function adaptBest(best: Record<ScorecardSeries, number | null>): Record<ScorecardSeries, number | null> {
  const adapted = {} as Record<ScorecardSeries, number | null>;
  SCORECARD_SERIES.forEach((series: ScorecardSeries) => (adapted[series] = best?.[series] ?? null));
  return adapted;
}

function adaptLinks(
  links: Record<ScorecardChart, DashboardLinkResponse | null>,
): Record<ScorecardChart, DashboardLink | null> {
  const adapted = {} as Record<ScorecardChart, DashboardLink | null>;
  Object.values(ScorecardChart).forEach((chart: ScorecardChart) => (adapted[chart] = adaptLink(links?.[chart])));
  return adapted;
}

export const adaptPartnerScorecard = (response: PartnerScorecardResponse): PartnerScorecard => ({
  asOfUtc: new Date(response.asOfUtc),
  window: { from: response.window.from, to: response.window.to },
  months: response.months.map(adaptMonth),
  best: adaptBest(response.best),
  declinedReasons: adaptDeclinedReasons(response.declinedReasons),
  links: adaptLinks(response.links),
});
